import { useQuery } from '@apollo/client'

import {
  ExportType,
  GetBrandStyleIdentifierDocument,
  accountExportIntegrationConfigsDocument,
} from '__generated__/atlas-types'

import { atlasClient } from 'graphql/client'

import {
  AccountExportIntegrationConfigs,
  ColumnConfig,
  TemplateConfigs,
  TemplateConstraint,
  UseTemplateTypes,
} from '../ImportOrders.types'
import { SimplifiedTypes, isTemplateConstraint } from './FirstStep.utils'

const formatTemplateConfigs = (
  templateConfigs: AccountExportIntegrationConfigs,
  accountTemplateConstraints: Array<TemplateConstraint> | null,
): TemplateConfigs => {
  if (!templateConfigs) {
    return {} as TemplateConfigs
  }

  return templateConfigs.reduce((mappedConfigs, config) => {
    const lowerCaseName = config.name.toLowerCase()

    const constraintSeparator = lowerCaseName.includes('-') ? '-' : '|'
    const constraint = lowerCaseName.includes(constraintSeparator)
      ? lowerCaseName.split(constraintSeparator)[1].trim()
      : lowerCaseName

    if (
      !isTemplateConstraint(constraint) ||
      (accountTemplateConstraints &&
        !accountTemplateConstraints.includes(constraint))
    ) {
      return mappedConfigs
    }

    const templateType = lowerCaseName.includes(SimplifiedTypes.Vertical)
      ? SimplifiedTypes.Vertical
      : SimplifiedTypes.Horizontal
    const columnConfig: ColumnConfig = JSON.parse(config.columnConfig)

    if (!mappedConfigs[templateType]) {
      mappedConfigs[templateType] = []
    }

    // ! asserts that mappedConfigs[templateType] is not undefined as checked above
    mappedConfigs[templateType]!.push({
      id: config.id,
      name: config.name,
      constraint,
      allColumns: columnConfig.columns.map((col) => col.column_name),
      requiredColumns: columnConfig.columns
        .filter((column) => column.required)
        .map((col) => col.column_name),
    })

    return mappedConfigs
  }, {} as TemplateConfigs)
}

export const useTemplateTypes = (
  accountTemplateConstraints: Array<TemplateConstraint> | null,
): UseTemplateTypes => {
  const { data, error, loading } = useQuery(
    accountExportIntegrationConfigsDocument,
    {
      client: atlasClient,
      variables: {
        accountId: undefined,
        integrationTypes: ExportType.ORDER_IMPORT,
      },
    },
  )

  const accountExportIntegrationConfigs: AccountExportIntegrationConfigs =
    !loading && !error && data?.accountExportIntegrationConfigs
      ? data.accountExportIntegrationConfigs
      : null

  return {
    templateConfigs: formatTemplateConfigs(
      accountExportIntegrationConfigs,
      accountTemplateConstraints,
    ),
    loadingTemplates: loading,
    templatesError: error,
  }
}

export const useGetBrandStyleIdentifiers = (skip?: boolean) => {
  const { data, loading, error } = useQuery(GetBrandStyleIdentifierDocument, {
    client: atlasClient,
    skip,
  })

  return {
    styleIdentifiers:
      !loading && !error && data
        ? (data.getBrandStyleIdentifier.styleIdentifiers as string[])
        : null,
    loadingStyleIdentifiers: loading,
    styleIdentifiersError: error,
  }
}

const isStyleNumberId = (styleIdentifiers?: string[] | null): boolean => {
  if (!Array.isArray(styleIdentifiers)) {
    return false
  }

  return styleIdentifiers.length === 1
    ? styleIdentifiers[0] === 'style_number'
    : false
}

export const useAccountTemplateConstraints = (
  styleIdentifiers: string[] | null,
  isLiteRetailer: boolean,
  useImportModal: boolean,
): Array<TemplateConstraint> | null => {
  if (isLiteRetailer) {
    return ['upc']
  }

  if (useImportModal) {
    return null
  }

  return isStyleNumberId(styleIdentifiers) ? null : ['upc']
}
